import React, { useState, useEffect, useCallback } from 'react'
import { get } from 'lodash'
import { useParams } from 'react-router-dom'
import {
  Button,
  ConfirmationPopup,
  ContentLoader,
  DescriptionTable,
  NotFound,
  Page,
  Tab,
  Tabs,
  TabContent,
  TabsHeader,
  Notification
} from 'shared/components'
import { sendErrorReport, displayValue, formatDate } from 'shared/helpers'
import { defaultDateTimeFormat } from 'shared/constants'
import {
  getDeviceDetails,
  resetDevice,
  blacklistDevice,
  whitelistDevice
} from 'src/devices/actions'
import LicensesTab from './LicensesTab'
import UsersTab from './UsersTab'
import './styles.scss'

const Device = () => {
  const { deviceID, companyId } = useParams()

  const [isLoading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [device, setDevice] = useState(null)
  const blacklisted = get(device, 'blacklisted', false)

  const [showResetDeviceConfirmation, setResetDeviceConfirmationDisplay] = useState(false)
  const [showBlacklistDeviceConfirmation, setBlacklistDeviceConfirmationDisplay] = useState(false)
  const [showWhitelistDeviceConfirmation, setWhitelistDeviceConfirmationDisplay] = useState(false)
  const [actionInProgress, setActionInProgress] = useState(false)

  const fetchDevice = useCallback(() => {
    getDeviceDetails(deviceID, companyId)
      .then(res => {
        const data = get(res, 'data')
        setDevice(data)
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot get device details')
        setLoading(false)
        setNotFound(true)
      })
  }, [companyId, deviceID])

  const refreshDevice = () => {
    setLoading(true)
    getDeviceDetails(deviceID, companyId)
      .then(res => {
        const data = get(res, 'data')
        setDevice(data)
        setLoading(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot refresh device details')
        setLoading(false)
        setNotFound(true)
      })
  }

  useEffect(() => {
    fetchDevice()
  }, [fetchDevice])

  const handleResetDevice = () => {
    setActionInProgress(true)

    resetDevice(deviceID, companyId)
      .then(() => {
        Notification('success', __('Device reset and license updated successfully.'))
        setActionInProgress(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot reset device')
        setActionInProgress(false)
        Notification(
          'error',
          __('Your changes were not saved'),
          __('There was an error while saving your changes')
        )
      })
      .finally(() => {
        refreshDevice() // Refresh device details after reset
        setResetDeviceConfirmationDisplay(false)
      })
  }
  const handleBlacklistDevice = () => {
    setActionInProgress(true)
    blacklistDevice(deviceID, companyId)
      .then(() => {
        Notification('success', __('Device blacklisted successfully'))
        setActionInProgress(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot blacklist device')
        setActionInProgress(false)
        Notification(
          'error',
          __('Your changes were not saved'),
          __('There was an error while saving your changes')
        )
      })
      .finally(() => {
        refreshDevice() // Refresh device details after blacklisting
        setBlacklistDeviceConfirmationDisplay(false)
      })
  }

  const handleWhitelistDevice = () => {
    setActionInProgress(true)
    whitelistDevice(deviceID, companyId)
      .then(() => {
        Notification('success', __('Device whitelisted successfully'))
        setActionInProgress(false)
      })
      .catch(err => {
        sendErrorReport(err, 'Cannot whitelist device')
        setActionInProgress(false)
        Notification(
          'error',
          __('Your changes were not saved'),
          __('There was an error while saving your changes')
        )
      })
      .finally(() => {
        refreshDevice() // Refresh device details after whitelisting
        setWhitelistDeviceConfirmationDisplay(false)
      })
  }

  if (isLoading) {
    return (
      <Page>
        <ContentLoader text={__('Getting device details')} />
      </Page>
    )
  }
  if (notFound) {
    return (
      <Page>
        <NotFound />
      </Page>
    )
  }

  return (
    <Page title={get(device, 'hardware_id', __('Device details'))}>
      <div className='list-header'>
        <div>
          <Button theme='error' size='sm' onClick={() => setResetDeviceConfirmationDisplay(true)}>
            {__('Reset')}
          </Button>
          {blacklisted ? (
            <Button
              theme='success'
              size='sm'
              onClick={() => setWhitelistDeviceConfirmationDisplay(true)}
              disabled={!get(device, 'blacklisted', false)}
            >
              {__('Whitelist')}
            </Button>
          ) : (
            <Button
              theme='default'
              size='sm'
              onClick={() => setBlacklistDeviceConfirmationDisplay(true)}
              disabled={get(device, 'blacklisted', false)}
            >
              {__('Blacklist')}
            </Button>
          )}
        </div>
      </div>
      <div className='Device'>
        <DescriptionTable
          details={[
            { label: __('Hardware ID'), value: displayValue(get(device, 'hardware_id')) },
            {
              label: __('First activated'),
              value: formatDate(get(device, 'time_activated'), defaultDateTimeFormat)
            },
            {
              label: __('Last checked'),
              value: formatDate(get(device, 'last_check'), defaultDateTimeFormat)
            },
            { label: __('OS'), value: displayValue(get(device, 'os')) },
            {
              label: __('SDK build'),
              value: displayValue(get(device, 'sdk_build_version'))
            },
            {
              label: __('App version'),
              value: displayValue(get(device, 'app_ver'))
            },
            {
              label: __('Hostname'),
              value: displayValue(get(device, 'hostname'))
            },
            {
              label: __('External IP address'),
              value: displayValue(get(device, 'external_ip'))
            },
            {
              label: __('IP address'),
              value: displayValue(get(device, 'ip'))
            },
            {
              label: __('MAC address'),
              value: displayValue(get(device, 'mac_address'))
            },
            {
              label: __('Is virtual machine'),
              value: get(device, 'is_vm') ? __('Yes') : __('No')
            },
            {
              label: __('VM info'),
              value: displayValue(get(device, 'vm_info'))
            }
          ]}
        />
        <Tabs>
          <TabsHeader>
            <Tab>{__('License')}</Tab>
            <Tab>{__('Users')}</Tab>
          </TabsHeader>
          <TabContent>
            <div className='licenses-tab'>
              <LicensesTab licenseID={get(device, 'license.id')} />
            </div>
          </TabContent>
          <TabContent>
            <UsersTab device={device} />
          </TabContent>
        </Tabs>
      </div>
      {showResetDeviceConfirmation && (
        <ConfirmationPopup
          closeCb={() => setResetDeviceConfirmationDisplay(false)}
          confirmCb={handleResetDevice}
          title={__('Are you sure you want to reset this device?')}
          confirmText={__('Reset')}
          theme='error'
          disabled={actionInProgress}
        >
          {__('Device will be reseted and licenses will be updated.')}
        </ConfirmationPopup>
      )}
      {showBlacklistDeviceConfirmation && (
        <ConfirmationPopup
          closeCb={() => setBlacklistDeviceConfirmationDisplay(false)}
          confirmCb={handleBlacklistDevice}
          title={__('Are you sure you want to blacklist this device?')}
          confirmText={__('Blacklist')}
          theme='error'
          disabled={actionInProgress}
        >
          {__('Device will be blacklisted.')}
        </ConfirmationPopup>
      )}
      {showWhitelistDeviceConfirmation && (
        <ConfirmationPopup
          closeCb={() => setWhitelistDeviceConfirmationDisplay(false)}
          confirmCb={handleWhitelistDevice}
          title={__('Are you sure you want to whitelist this device?')}
          confirmText={__('Whitelist')}
          theme='success'
          disabled={actionInProgress}
        >
          {__('Device will be whitelisted.')}
        </ConfirmationPopup>
      )}
    </Page>
  )
}

export default Device
